import { useEffect } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { useSelector, useDispatch } from 'react-redux'
import {
  Box,
  Button,
  CircularProgress,
  Paper,
  Stack,
  Typography,
  alpha,
  useTheme
} from '@mui/material'

import { brand } from '../config'
import { authActions } from '../redux'

const GoogleGlyph = () => (
  <Box
    component='svg'
    viewBox='0 0 48 48'
    aria-hidden
    sx={{ width: 20, height: 20, flexShrink: 0 }}
  >
    <path fill='#FFC107' d='M43.6 20.5H42V20H24v8h11.3C33.8 32.3 29.3 36 24 36c-6.6 0-12-5.4-12-12S17.4 12 24 12c3 0 5.7 1.1 7.8 3l5.7-5.7C33.8 6.3 29.2 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 19.5-9 19.5-20c0-1.2-.1-2.3-.4-3.5z' />
    <path fill='#FF3D00' d='M6.3 14.7l6.6 4.8C14.7 16.2 19 12 24 12c3 0 5.7 1.1 7.8 3l5.7-5.7C33.8 6.3 29.2 4 24 4 16.5 4 10 8.3 6.3 14.7z' />
    <path fill='#4CAF50' d='M24 44c5.2 0 9.8-2 13.1-5.2l-6.1-5.1C29 35.6 26.6 36 24 36c-5.2 0-9.7-3.4-11.3-8.1l-6.5 5C9.9 39.7 16.4 44 24 44z' />
    <path fill='#1976D2' d='M43.6 20.5H42V20H24v8h11.3c-1.1 3.2-3.5 5.9-6.2 7.7l.1.1 6.1 5.1C38.6 37.9 44 32 44 24c0-1.2-.1-2.3-.4-3.5z' />
  </Box>
)

const LoginView = () => {
  const theme = useTheme()
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const location = useLocation()
  const { loading: authLoading, isAuthenticated, ready: authReady } = useSelector((state) => state.auth)
  const from = location.state?.from?.pathname || '/'
  const accent = theme.custom?.keysys?.blue || theme.palette.primary.main
  const ink = theme.custom?.keysys?.dark || '#41464b'

  useEffect(() => {
    if (authReady && !authLoading && isAuthenticated) {
      navigate(from, { replace: true })
    }
  }, [authReady, authLoading, isAuthenticated, from, navigate])

  const handleGoogle = () => {
    dispatch(authActions.signInWithGoogle(() => {
      navigate(from, { replace: true })
    }))
  }

  if (!authReady || authLoading) {
    return null
  }

  if (isAuthenticated) {
    return null
  }

  return (
    <Box
      component='main'
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        px: 2,
        py: 4,
        position: 'relative',
        overflow: 'hidden',
        backgroundColor: ink,
        backgroundImage: `
          radial-gradient(ellipse 90% 70% at 12% -10%, ${alpha(accent, 0.28)} 0%, transparent 55%),
          radial-gradient(ellipse 70% 55% at 100% 100%, ${alpha(accent, 0.16)} 0%, transparent 50%),
          linear-gradient(165deg, ${alpha('#0b0d10', 0.92)} 0%, ${alpha(ink, 0.98)} 48%, ${alpha('#1a1f24', 1)} 100%)
        `
      }}
    >
      <Box
        aria-hidden
        sx={{
          position: 'absolute',
          inset: 0,
          opacity: 0.35,
          backgroundImage: `linear-gradient(${alpha('#fff', 0.03)} 1px, transparent 1px), linear-gradient(90deg, ${alpha('#fff', 0.03)} 1px, transparent 1px)`,
          backgroundSize: '48px 48px',
          maskImage: 'radial-gradient(ellipse 70% 60% at 50% 40%, #000 20%, transparent 75%)',
          pointerEvents: 'none'
        }}
      />

      <Stack
        spacing={3}
        alignItems='center'
        sx={{ width: '100%', maxWidth: 440, position: 'relative', zIndex: 1 }}
      >
        <Stack spacing={1.75} alignItems='center' textAlign='center'>
          <Box
            component='img'
            src='/keysys_logo_white.png'
            alt='Keysys'
            sx={{
              height: { xs: 56, sm: 64 },
              width: 'auto',
              display: 'block',
              filter: 'drop-shadow(0 8px 24px rgba(0,0,0,0.35))'
            }}
          />
          <Typography
            component='h1'
            sx={{
              fontWeight: 900,
              fontSize: { xs: '2.4rem', sm: '2.85rem' },
              letterSpacing: { xs: 6, sm: 8 },
              lineHeight: 1,
              color: alpha('#fff', 0.98),
              textShadow: `0 0 40px ${alpha(accent, 0.35)}`
            }}
          >
            {brand.name}
          </Typography>
          <Typography
            sx={{
              fontWeight: 600,
              fontSize: { xs: '0.78rem', sm: '0.84rem' },
              letterSpacing: 0.4,
              color: alpha('#fff', 0.58),
              maxWidth: 360,
              lineHeight: 1.45
            }}
          >
            {brand.expansion}
          </Typography>
        </Stack>

        <Paper
          elevation={0}
          sx={{
            width: '100%',
            p: { xs: 2.5, sm: 3 },
            borderRadius: 3,
            border: `1px solid ${alpha(accent, 0.28)}`,
            backgroundColor: alpha('#0c0e12', 0.72),
            backgroundImage: `linear-gradient(160deg, ${alpha(accent, 0.14)} 0%, ${alpha('#000', 0.2)} 55%, transparent 100%)`,
            boxShadow: `0 24px 64px ${alpha('#000', 0.45)}, inset 0 1px 0 ${alpha('#fff', 0.06)}`,
            backdropFilter: 'blur(14px)'
          }}
        >
          {authLoading
            ? (
              <Stack spacing={1.5} alignItems='center' sx={{ py: 3 }}>
                <CircularProgress size={32} sx={{ color: accent }} />
                <Typography sx={{ color: alpha('#fff', 0.7), fontWeight: 600 }}>
                  Signing you in…
                </Typography>
              </Stack>
              )
            : (
              <Stack spacing={2}>
                <Box>
                  <Typography
                    sx={{
                      fontWeight: 800,
                      fontSize: '1.15rem',
                      color: alpha('#fff', 0.95),
                      mb: 0.5
                    }}
                  >
                    Sign in
                  </Typography>
                  <Typography
                    variant='body2'
                    sx={{ color: alpha('#fff', 0.58), lineHeight: 1.5 }}
                  >
                    Use your Keysys Google account to open the support operations console.
                  </Typography>
                </Box>

                <Button
                  fullWidth
                  size='large'
                  variant='contained'
                  onClick={handleGoogle}
                  disabled={authLoading}
                  startIcon={<GoogleGlyph />}
                  sx={{
                    py: 1.35,
                    borderRadius: 2,
                    fontWeight: 800,
                    fontSize: '0.95rem',
                    color: '#1a1a1a',
                    backgroundColor: '#fff',
                    boxShadow: `0 8px 24px ${alpha(accent, 0.25)}`,
                    '&:hover': {
                      backgroundColor: alpha('#fff', 0.92),
                      boxShadow: `0 10px 28px ${alpha(accent, 0.35)}`
                    },
                    '&.Mui-disabled': {
                      backgroundColor: alpha('#fff', 0.5),
                      color: alpha('#000', 0.45)
                    }
                  }}
                >
                  Sign in with Google
                </Button>
              </Stack>
              )}
        </Paper>

        <Typography
          variant='caption'
          sx={{
            color: alpha('#fff', 0.38),
            letterSpacing: 0.2,
            textAlign: 'center'
          }}
        >
          {brand.shortDescription}
        </Typography>
      </Stack>
    </Box>
  )
}

export { LoginView }
